<template>
  <main
    id="orders-page"
    class="page-container"
    v-if="$route.name === 'carico-merce'"
  >
    <div class="table-container-orders">
      <!-- Toolbar -->
      <div class="orders-toolbar">
        <button class="primary-btn" @click="openCreateModal">
          <i class="fa-solid fa-plus"></i> Nuovo carico merce
        </button>
        <button
          class="refresh-btn"
          :class="{ refreshed }"
          @click="handleRefresh"
          :disabled="refreshing"
          title="Aggiorna"
        >
          <i
            :class="[
              'fa-solid',
              refreshed ? 'fa-check' : 'fa-arrows-rotate',
              { 'fa-spin': refreshing },
            ]"
          ></i>
          <span>{{
            refreshing ? "Aggiorna…" : refreshed ? "Aggiornato!" : "Aggiorna"
          }}</span>
        </button>
      </div>

      <!-- Tabs -->
      <div class="orders-tabs">
        <button
          :class="['tab-btn', { active: activeTab === 'da_gestire' }]"
          @click="switchTab('da_gestire')"
        >
          Da gestire
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'completato' }]"
          @click="switchTab('completato')"
        >
          Completati
        </button>
      </div>

      <!-- Tabella -->
      <div class="tbl-wrap" :class="{ dimmed: isLoading }">
        <table class="tbl">
          <thead>
            <tr>
              <th>Codice</th>
              <th>Nome articolo</th>
              <th>Categoria</th>
              <th>Stato</th>
              <th>Quantità</th>
              <th>Data carico</th>
              <th>Note</th>
              <th style="text-align: left">Azioni</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in visibleOrders" :key="order.id">
              <td class="td-name" :title="order.item.itemCode">
                {{ order.item.itemCode }}
              </td>
              <td
                class="td-name"
                :title="order.item.itemName || order.item.brand || ''"
              >
                {{ order.item.itemName || order.item.brand || "—" }}
              </td>
              <td>{{ order.item.category }}</td>
              <td>
                <span :class="['status-badge', order.status]">
                  {{ statusLabel(order.status) }}
                </span>
              </td>
              <td>{{ quantityLabel(order) }}</td>
              <td>{{ formatDate(order.orderedAt) }}</td>
              <td class="notes-cell" :title="order.notes || ''">
                {{ order.notes || "—" }}
              </td>
              <td>
                <div class="actions-cell">
                  <!-- Modifica qty/note: creato, in_sospeso, confermato -->
                  <button
                    v-if="
                      ['creato', 'in_sospeso', 'confermato'].includes(
                        order.status
                      )
                    "
                    class="icons-button"
                    @click="openEditModal(order)"
                    title="Modifica carico merce"
                    type="button"
                  >
                    <i class="fa-solid fa-pen-to-square"></i>
                  </button>

                  <!-- Split→Complete: creato e in_sospeso → modale 2 step -->
                  <button
                    v-if="
                      order.status === 'creato' || order.status === 'in_sospeso'
                    "
                    class="icons-button"
                    @click="openSplitModal(order)"
                    title="Ricevi merce"
                    type="button"
                  >
                    <i class="fa-solid fa-truck-ramp-box"></i>
                  </button>

                  <!-- Complete diretto: confermato → modale semplificato (step 2) -->
                  <button
                    v-if="order.status === 'confermato'"
                    class="icons-button"
                    @click="openSplitModal(order)"
                    title="Ricevi merce"
                    type="button"
                  >
                    <i class="fa-solid fa-circle-check"></i>
                  </button>

                  <!-- Riordina merce danneggiata: completato con damagedQty > 0 -->
                  <button
                    v-if="
                      order.status === 'completato' &&
                      order.damagedQty > 0 &&
                      !order.isResolved
                    "
                    class="icons-button"
                    @click="openReorderModal(order)"
                    title="Riordina merce danneggiata"
                    type="button"
                  >
                    <i class="fa-solid fa-rotate-right"></i>
                  </button>

                  <!-- Archivia: sempre, con restrizione deliveredQty per non-completato -->
                  <button
                    v-if="
                      order.status === 'completato' || order.deliveredQty === 0
                    "
                    class="icons-button icons-button--danger"
                    @click="openArchiveModal(order)"
                    title="Archivia carico merce"
                    type="button"
                  >
                    <i class="fa-solid fa-trash-can"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!isLoading && visibleOrders.length === 0">
              <td colspan="7" class="no-result">
                Nessun carico merce trovato.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginazione -->
      <div class="pagination">
        <div class="pagination-left">
          <div class="search-container">
            <i
              v-if="!searchTerm"
              class="fa-solid fa-magnifying-glass search-icon"
            ></i>
            <input
              type="text"
              v-model="searchTerm"
              @keyup.enter="onSearch"
              placeholder="Cerca carichi merce..."
              class="search-box"
            />
            <button
              v-if="searchTerm"
              @click="onResetSearch"
              class="clear-search-button"
              :disabled="isLoading"
              title="Cancella ricerca"
              type="button"
            >
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
        </div>

        <div class="pagination-right">
          <div class="pagination-info">
            {{ paginationState.startItem || 0 }}–{{
              paginationState.endItem || 0
            }}
            di
            {{ paginationState.totalItems || 0 }}
          </div>
          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToPage(1)"
              :disabled="paginationState.currentPage === 1"
              title="Prima pagina"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(paginationState.currentPage - 1)"
              :disabled="paginationState.currentPage === 1"
              title="Pagina precedente"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button
              v-for="pageNum in visiblePages"
              :key="pageNum"
              @click="goToPage(pageNum)"
              :class="{ active: paginationState.currentPage === pageNum }"
              class="pages-button"
            >
              {{ pageNum }}
            </button>
            <button
              class="icons-button"
              @click="goToPage(paginationState.currentPage + 1)"
              :disabled="
                paginationState.currentPage === paginationState.totalPages
              "
              title="Pagina successiva"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(paginationState.totalPages)"
              :disabled="
                paginationState.currentPage === paginationState.totalPages
              "
              title="Ultima pagina"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- MODALE: Modifica ordine (in_attesa) -->
      <VueFinalModal
        v-model="showEditModal"
        :clickToClose="true"
        :escToClose="true"
        classes="archive-modal"
        content-class="archive-modal-content"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="showEditModal = false"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Modifica carico merce</h3>
          <div class="modal-form">
            <label>
              Quantità ({{
                editingOrder && editingOrder.item.category === "piastrelle"
                  ? "scatole"
                  : "pezzi"
              }})
            </label>
            <input
              type="number"
              v-model.number="editForm.quantity"
              min="1"
              class="modal-input"
            />
            <label>Note</label>
            <textarea
              v-model="editForm.notes"
              class="modal-textarea"
              rows="3"
            ></textarea>
          </div>
          <div class="modal-actions">
            <button
              class="btn btn-secondary"
              @click="showEditModal = false"
              :disabled="editBusy"
            >
              Annulla
            </button>
            <button
              class="btn btn-primary"
              @click="confirmEdit"
              :disabled="editBusy"
            >
              {{ editBusy ? "Salvando..." : "Salva" }}
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE: Split → Complete -->
      <VueFinalModal
        v-model="showSplitModal"
        :clickToClose="!splitBusy"
        :escToClose="!splitBusy"
        classes="archive-modal"
        content-class="archive-modal-content"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="showSplitModal = false"
            :disabled="splitBusy"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">
            Ricevi merce —
            {{ splittingOrder && splittingOrder.item.itemCode }}
          </h3>

          <!-- Step 1: suddivisione quantità -->
          <template v-if="splitStep === 1">
            <div class="modal-form">
              <p class="modal_subtitle" style="margin: 0">
                Quantità totale:
                <strong>{{ splittingOrder && splittingOrder.quantity }}</strong>
                · Già ricevuti:
                <strong>{{
                  splittingOrder && splittingOrder.deliveredQty
                }}</strong>
                · Rimanenti:
                <strong>{{
                  splittingOrder &&
                  splittingOrder.quantity - splittingOrder.deliveredQty
                }}</strong>
              </p>
              <label>Ricevi ora</label>
              <input
                type="number"
                v-model.number="splitForm.confirmedQty"
                @input="onConfirmedQtyInput"
                min="0"
                :max="
                  splittingOrder &&
                  splittingOrder.quantity - splittingOrder.deliveredQty
                "
                class="modal-input"
              />
              <label>Rinvia a dopo (calcolato automaticamente)</label>
              <input
                type="number"
                :value="splitForm.suspendedQty"
                readonly
                class="modal-input"
                style="background: #f7fafc; color: #718096"
              />
            </div>
            <div class="modal-actions">
              <button
                class="btn btn-secondary"
                @click="showSplitModal = false"
                :disabled="splitBusy"
              >
                Annulla
              </button>
              <button
                class="btn btn-primary"
                @click="goToSplitStep2"
                :disabled="splitBusy"
              >
                Avanti →
              </button>
            </div>
          </template>

          <!-- Step 2: riepilogo e conferma -->
          <template v-if="splitStep === 2">
            <p class="modal_subtitle">
              Stai per ricevere
              <strong>{{ splitForm.confirmedQty }}</strong> pz. Il magazzino
              verrà aggiornato automaticamente.
              <template v-if="splitForm.suspendedQty > 0">
                <br />Resteranno
                <strong>{{ splitForm.suspendedQty }}</strong> pz da ricevere in
                un secondo round.
              </template>
              <template v-else>
                <br /><small class="warning-text"
                  >Il carico merce verrà chiuso definitivamente.</small
                >
              </template>
            </p>
            <div class="modal-form" style="margin-top: 0">
              <label>
                Pezzi danneggiati all'arrivo
                <span style="font-weight: 400; color: #718096"
                  >(opzionale)</span
                >
              </label>
              <input
                type="number"
                v-model.number="splitForm.damagedQty"
                min="0"
                :max="splitForm.confirmedQty"
                class="modal-input"
              />
              <p
                v-if="splitForm.damagedQty > 0"
                class="modal_subtitle"
                style="margin: 0; color: #c62828; font-size: 13px"
              >
                Di cui {{ splitForm.damagedQty }} danneggiati — solo
                {{ splitForm.confirmedQty - splitForm.damagedQty }} andranno in
                magazzino.
              </p>
            </div>
            <div class="modal-actions">
              <button
                v-if="splittingOrder && splittingOrder.status !== 'confermato'"
                class="btn btn-secondary"
                @click="splitStep = 1"
                :disabled="splitBusy"
              >
                ← Indietro
              </button>
              <button
                v-else
                class="btn btn-secondary"
                @click="showSplitModal = false"
                :disabled="splitBusy"
              >
                Annulla
              </button>
              <button
                class="btn btn-primary delivered-confirm"
                @click="confirmSplitComplete"
                :disabled="splitBusy"
              >
                {{ splitBusy ? "Ricevendo..." : "Conferma ricezione" }}
              </button>
            </div>
          </template>
        </template>
      </VueFinalModal>

      <!-- MODALE: Archiviazione -->
      <VueFinalModal
        v-model="showArchiveModal"
        :clickToClose="true"
        :escToClose="true"
        classes="archive-modal"
        content-class="archive-modal-content"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="showArchiveModal = false"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Archivia carico merce</h3>
          <p class="modal_subtitle">
            Archiviare il carico merce di
            <strong>
              {{ archivingOrder && archivingOrder.item.itemCode }} —
              {{
                archivingOrder &&
                (archivingOrder.item.itemName ||
                  archivingOrder.item.brand ||
                  "")
              }} </strong
            >?
          </p>
          <div class="modal-actions">
            <button
              class="btn btn-secondary"
              @click="showArchiveModal = false"
              :disabled="archiveBusy"
            >
              Annulla
            </button>
            <button
              class="btn btn-primary"
              @click="confirmArchive"
              :disabled="archiveBusy"
            >
              {{ archiveBusy ? "Archiviando..." : "Archivia" }}
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE: Riordino merce danneggiata -->
      <VueFinalModal
        v-model="showReorderModal"
        :clickToClose="!reorderBusy"
        :escToClose="!reorderBusy"
        classes="archive-modal"
        content-class="archive-modal-content"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="showReorderModal = false"
            :disabled="reorderBusy"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Riordina merce danneggiata</h3>
          <p class="modal_subtitle">
            Riordinare
            <strong>{{ reorderingOrder && reorderingOrder.damagedQty }}</strong>
            pz di
            <strong>{{
              reorderingOrder && reorderingOrder.item.itemCode
            }}</strong
            >? <br /><small style="color: #718096"
              >Verrà creato un nuovo carico merce con nota pre-compilata.</small
            >
          </p>
          <div class="modal-actions">
            <button
              class="btn btn-secondary"
              @click="showReorderModal = false"
              :disabled="reorderBusy"
            >
              Annulla
            </button>
            <button
              class="btn btn-primary"
              @click="confirmReorder"
              :disabled="reorderBusy"
            >
              {{ reorderBusy ? "Creando..." : "Riordina" }}
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE: Crea nuovo ordine -->
      <VueFinalModal
        v-model="showCreateModal"
        :clickToClose="true"
        :escToClose="true"
        classes="archive-modal"
        content-class="order-create-modal-content"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="showCreateModal = false"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Nuovo carico merce</h3>
          <div class="modal-form">
            <label>Articolo</label>
            <!-- Articolo già selezionato -->
            <div v-if="selectedItem" class="item-selected-row">
              <span class="item-selected-label">
                {{ selectedItem.itemCode }} —
                {{ selectedItem.itemName || selectedItem.brand || "—" }}
                <span
                  class="item-category-badge"
                  :class="categoryBadgeClass(selectedItem.category)"
                >
                  {{ selectedItem.category || "—" }}
                </span>
              </span>
              <button
                type="button"
                class="item-deselect-btn icons-button"
                @click="clearItemSelection"
                title="Cambia articolo"
              >
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
            <!-- Ricerca articolo -->
            <div v-else class="item-search-wrap">
              <input
                type="text"
                v-model="itemSearch"
                @input="onItemSearch"
                placeholder="Cerca codice, nome..."
                class="modal-input"
                autocomplete="off"
              />
              <div v-if="itemSearchLoading" class="item-search-hint">
                Ricerca...
              </div>
              <ul v-else-if="itemSearchResults.length" class="item-search-list">
                <li
                  v-for="item in itemSearchResults"
                  :key="item.id"
                  @click="selectItem(item)"
                  class="item-search-row"
                  :class="{ 'zero-stock': !hasWarehouseStock(item) }"
                >
                  <span class="item-search-code">{{ item.itemCode }}</span>
                  <span class="item-search-name">{{
                    item.itemName || item.brand || "—"
                  }}</span>
                  <span
                    class="item-category-badge"
                    :class="categoryBadgeClass(item.category)"
                  >
                    {{ item.category || "—" }}
                  </span>
                  <span class="item-search-stock">{{
                    warehouseStockLabel(item)
                  }}</span>
                </li>
              </ul>
              <div
                v-else-if="itemSearch && !itemSearchLoading"
                class="item-search-hint"
              >
                Nessun articolo trovato.
              </div>
            </div>
            <label>Quantità</label>
            <input
              type="number"
              v-model.number="createForm.quantity"
              min="1"
              class="modal-input"
            />
            <label
              >Note
              <span style="font-weight: 400; color: #718096"
                >(opzionale)</span
              ></label
            >
            <textarea
              v-model="createForm.notes"
              class="modal-textarea"
              rows="3"
            ></textarea>
          </div>
          <div class="modal-actions">
            <button
              class="btn btn-secondary"
              @click="showCreateModal = false"
              :disabled="createBusy"
            >
              Annulla
            </button>
            <button
              class="btn btn-primary"
              @click="confirmCreate"
              :disabled="
                createBusy || !createForm.itemId || !createForm.quantity
              "
            >
              {{ createBusy ? "Creando..." : "Crea carico merce" }}
            </button>
          </div>
        </template>
      </VueFinalModal>
    </div>
  </main>
  <router-view></router-view>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import { formatToLocalDate } from "../utilities/formatting";

export default {
  name: "OrderPage",
  computed: {
    ...mapGetters({
      allOrders: "orders/list",
      ordersPagination: "orders/pagination",
      allItems: "items/list",
    }),
    normalizedSearch() {
      return this.searchTerm.trim().toLowerCase();
    },
    isSearching() {
      return this.normalizedSearch.length > 0;
    },
    isClientSide() {
      return this.isSearching || this.activeTab === "da_gestire";
    },
    filteredOrders() {
      if (!this.isSearching) return this.allOrders;
      const q = this.normalizedSearch;
      return this.allOrders.filter((order) => {
        const item = order.item || {};
        return [
          item.itemCode,
          item.itemName,
          item.brand,
          item.category,
          order.notes,
          this.formatDate(order.orderedAt),
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));
      });
    },
    visibleOrders() {
      if (!this.isClientSide) return this.allOrders;
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredOrders.slice(start, start + this.pageSize);
    },
    paginationState() {
      if (!this.isClientSide) return this.ordersPagination;
      const totalItems = this.filteredOrders.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / this.pageSize));
      const currentPage = Math.min(this.currentPage, totalPages);
      const startItem = totalItems ? (currentPage - 1) * this.pageSize + 1 : 0;
      const endItem = totalItems
        ? Math.min(currentPage * this.pageSize, totalItems)
        : 0;
      return {
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage: this.pageSize,
        startItem,
        endItem,
      };
    },
    visiblePages() {
      const range = 2;
      const current = this.paginationState.currentPage || 1;
      const total = this.paginationState.totalPages || 1;
      const start = Math.max(1, current - range);
      const end = Math.min(total, current + range);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  data() {
    return {
      isLoading: false,
      refreshing: false,
      refreshed: false,
      activeTab: "da_gestire",
      currentPage: 1,
      pageSize: 7,
      searchTerm: "",

      showEditModal: false,
      editingOrder: null,
      editForm: { quantity: 1, notes: "" },
      editBusy: false,

      showSplitModal: false,
      splittingOrder: null,
      splitStep: 1,
      splitForm: { confirmedQty: 0, suspendedQty: 0, damagedQty: 0 },
      splitBusy: false,

      showArchiveModal: false,
      archivingOrder: null,
      archiveBusy: false,

      showReorderModal: false,
      reorderingOrder: null,
      reorderBusy: false,

      showCreateModal: false,
      createForm: { itemId: "", quantity: 1, notes: "" },
      createBusy: false,

      selectedItem: null,
      itemSearch: "",
      itemSearchResults: [],
      itemSearchLoading: false,
    };
  },
  methods: {
    async handleRefresh() {
      this.refreshing = true;
      this.refreshed = false;
      try {
        await this.getOrders(this.currentPage);
      } finally {
        this.refreshing = false;
        this.refreshed = true;
        setTimeout(() => {
          this.refreshed = false;
        }, 1500);
      }
    },

    ...mapActions([
      "fetchOrders",
      "fetchActiveOrders",
      "splitOrder",
      "completeOrder",
      "createOrder",
      "updateOrder",
      "archiveOrder",
      "fetchItems",
    ]),

    formatDate: formatToLocalDate,

    statusLabel(status) {
      const map = {
        creato: "Creato",
        in_sospeso: "In sospeso",
        confermato: "Confermato",
        completato: "Completato",
      };
      return map[status] || status;
    },

    quantityLabel(order) {
      const unit = order.item.category === "piastrelle" ? "scatole" : "pezzi";
      const { quantity, confirmedQty, suspendedQty, deliveredQty, status } =
        order;
      if (status === "completato") {
        const damaged = order.damagedQty;
        if (damaged > 0) {
          return `${
            deliveredQty - damaged
          } / ${quantity} ricevuti · ${damaged} danneggiati`;
        }
        return `${deliveredQty} / ${quantity} ricevuti`;
      }
      if (status === "confermato") {
        return `${confirmedQty} confermati · ${suspendedQty} sospesi`;
      }
      if (status === "in_sospeso" && deliveredQty > 0) {
        const damaged = order.damagedQty;
        const remaining = quantity - deliveredQty;
        if (damaged > 0) {
          return `${
            deliveredQty - damaged
          } / ${quantity} in magazzino · ${damaged} danneggiati`;
        }
        return `${deliveredQty} / ${quantity} ricevuti · ${remaining} in sospeso`;
      }
      return `${quantity} ${unit}`;
    },

    warehouseCount(item) {
      const stock = Number(item?.inWarehouse);
      return Number.isFinite(stock) ? stock : 0;
    },

    hasWarehouseStock(item) {
      return this.warehouseCount(item) > 0;
    },

    warehouseStockLabel(item) {
      const stock = this.warehouseCount(item);
      return stock > 0 ? `${stock} disp.` : "Esaurito";
    },

    categoryBadgeClass(category) {
      const normalized = String(category || "").toLowerCase();
      return {
        "is-tiles": normalized === "piastrelle",
        "is-sanitary": normalized === "sanitari",
      };
    },

    async fetchItemDetail(itemId) {
      const { data } = await axiosInstance.get(`item/${itemId}`);
      return data?.item || null;
    },

    async validateOrderCompletion(order) {
      const itemId = order?.item?.id;
      if (!itemId) return true;

      const item = await this.fetchItemDetail(itemId);
      const category = String(item?.category || order?.item?.category || "")
        .trim()
        .toLowerCase();

      if (category !== "piastrelle") return true;

      const piecesPerBox = Number(item?.piecesPerBox);
      if (Number.isFinite(piecesPerBox) && piecesPerBox > 0) return true;

      this.$toast.error(
        "Completamento bloccato: per questa piastrella manca 'Pezzi per scatola'. Aggiorna l'articolo prima di completare il carico merce."
      );
      return false;
    },

    async getOrders(page = 1) {
      this.isLoading = true;
      try {
        if (this.activeTab === "da_gestire") {
          await this.fetchActiveOrders();
        } else {
          await this.fetchOrders({
            page: this.isSearching ? 1 : page,
            limit: this.isSearching ? 10000 : this.pageSize,
            status: this.activeTab,
          });
        }
        this.currentPage = page;
      } catch (err) {
        if (err?.response?.status === 404 && page !== 1) {
          await this.getOrders(1);
          return;
        }
        this.$toast.error(
          err?.response?.data?.message ||
            "Errore nel caricamento carichi merce."
        );
      } finally {
        this.isLoading = false;
      }
    },

    async switchTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.searchTerm = "";
      await this.getOrders(1);
    },

    async onSearch() {
      await this.getOrders(1);
    },

    async onResetSearch() {
      this.searchTerm = "";
      await this.getOrders(1);
    },

    async goToPage(page) {
      const total = this.paginationState.totalPages || 1;
      if (page < 1 || page > total) return;
      await this.getOrders(page);
    },

    // --- Edit ---
    openEditModal(order) {
      this.editingOrder = order;
      this.editForm = { quantity: order.quantity, notes: order.notes || "" };
      this.showEditModal = true;
    },
    async confirmEdit() {
      if (!this.editingOrder) return;
      this.editBusy = true;
      try {
        await this.updateOrder({
          id: this.editingOrder.id,
          payload: {
            quantity: this.editForm.quantity,
            notes: this.editForm.notes,
          },
        });
        this.$toast.success("Carico merce aggiornato con successo.");
        this.showEditModal = false;
        await this.getOrders(this.currentPage);
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Errore durante la modifica."
        );
      } finally {
        this.editBusy = false;
      }
    },

    // --- Split → Complete ---
    openSplitModal(order) {
      this.splittingOrder = order;
      this.splitBusy = false;
      if (order.status === "confermato") {
        this.splitStep = 2;
        this.splitForm = {
          confirmedQty: order.confirmedQty,
          suspendedQty: order.suspendedQty,
          damagedQty: 0,
        };
      } else {
        this.splitStep = 1;
        const remaining = order.quantity - (order.deliveredQty || 0);
        this.splitForm = {
          confirmedQty: remaining,
          suspendedQty: 0,
          damagedQty: 0,
        };
      }
      this.showSplitModal = true;
    },

    onConfirmedQtyInput() {
      if (!this.splittingOrder) return;
      const remaining =
        this.splittingOrder.quantity - (this.splittingOrder.deliveredQty || 0);
      const confirmed = Math.max(
        0,
        Math.min(remaining, Number(this.splitForm.confirmedQty) || 0)
      );
      this.splitForm.confirmedQty = confirmed;
      this.splitForm.suspendedQty = remaining - confirmed;
    },

    async goToSplitStep2() {
      const remaining =
        this.splittingOrder.quantity - (this.splittingOrder.deliveredQty || 0);
      const { confirmedQty, suspendedQty } = this.splitForm;
      if (confirmedQty < 0 || suspendedQty < 0) {
        this.$toast.error("Le quantità non possono essere negative.");
        return;
      }
      if (confirmedQty + suspendedQty !== remaining) {
        this.$toast.error(
          `La somma deve essere ${remaining} (rimanente da ricevere).`
        );
        return;
      }
      if (confirmedQty === 0 && suspendedQty === 0) {
        this.$toast.error("Almeno una quantità deve essere maggiore di zero.");
        return;
      }
      this.splitStep = 2;
    },

    async confirmSplitComplete() {
      if (!this.splittingOrder) return;
      this.splitBusy = true;
      try {
        const canComplete = await this.validateOrderCompletion(
          this.splittingOrder
        );
        if (!canComplete) return;

        if (this.splittingOrder.status !== "confermato") {
          await this.splitOrder({
            id: this.splittingOrder.id,
            confirmedQty: this.splitForm.confirmedQty,
            suspendedQty: this.splitForm.suspendedQty,
          });
        }

        if (this.splitForm.confirmedQty > 0) {
          const damagedQty = this.splitForm.damagedQty || 0;
          if (
            !Number.isInteger(damagedQty) ||
            damagedQty < 0 ||
            damagedQty > this.splitForm.confirmedQty
          ) {
            this.$toast.error(
              `Pezzi danneggiati non validi (deve essere un intero tra 0 e ${this.splitForm.confirmedQty}).`
            );
            return;
          }
          const completedOrder = { ...this.splittingOrder, damagedQty };
          await this.completeOrder({
            id: this.splittingOrder.id,
            damagedQty,
          });
          this.$toast.success("Merce ricevuta. Magazzino aggiornato.");
          this.showSplitModal = false;
          await this.getOrders(this.currentPage);
          if (damagedQty > 0) {
            this.openReorderModal(completedOrder);
          }
          return;
        } else {
          this.$toast.success("Carico merce sospeso.");
        }

        this.showSplitModal = false;
        await this.getOrders(this.currentPage);
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Errore durante la ricezione."
        );
      } finally {
        this.splitBusy = false;
      }
    },

    // --- Archive ---
    openArchiveModal(order) {
      this.archivingOrder = order;
      this.showArchiveModal = true;
    },
    async confirmArchive() {
      if (!this.archivingOrder) return;
      this.archiveBusy = true;
      try {
        await this.archiveOrder(this.archivingOrder.id);
        this.$toast.success("Carico merce archiviato con successo.");
        this.showArchiveModal = false;
        await this.getOrders(this.currentPage);
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Errore durante l'archiviazione."
        );
      } finally {
        this.archiveBusy = false;
      }
    },

    // --- Reorder ---
    openReorderModal(order) {
      this.reorderingOrder = order;
      this.reorderBusy = false;
      this.showReorderModal = true;
    },

    async confirmReorder() {
      if (!this.reorderingOrder) return;
      this.reorderBusy = true;
      try {
        await this.createOrder({
          itemId: this.reorderingOrder.item.id,
          quantity: this.reorderingOrder.damagedQty,
          notes: `Riordino merce danneggiata – carico merce #${this.reorderingOrder.id}`,
          sourceOrderId: this.reorderingOrder.id,
        });
        this.$toast.success("Riordine carico merce creato con successo!");
        this.showReorderModal = false;
        this.activeTab = "da_gestire";
        await this.getOrders(1);
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            "Errore durante il riordine carico merce."
        );
      } finally {
        this.reorderBusy = false;
      }
    },

    // --- Create ---
    async openCreateModal() {
      this.createForm = { itemId: "", quantity: 1, notes: "" };
      this.selectedItem = null;
      this.itemSearch = "";
      this.itemSearchResults = [];
      this.showCreateModal = true;
      await this.loadItemSuggestions("");
    },

    async loadItemSuggestions(search) {
      this.itemSearchLoading = true;
      try {
        await this.fetchItems({
          page: 1,
          limit: 50,
          ...(search ? { search } : {}),
        });
        const sorted = [...this.allItems].sort(
          (a, b) =>
            Number(this.hasWarehouseStock(b)) -
            Number(this.hasWarehouseStock(a))
        );
        this.itemSearchResults = sorted.slice(0, 5);
      } catch {
        // ignora
      } finally {
        this.itemSearchLoading = false;
      }
    },

    onItemSearch() {
      clearTimeout(this._itemSearchTimer);
      this._itemSearchTimer = setTimeout(() => {
        this.loadItemSuggestions(this.itemSearch);
      }, 300);
    },

    selectItem(item) {
      this.createForm.itemId = item.id;
      this.selectedItem = item;
      this.itemSearch = "";
    },

    clearItemSelection() {
      this.createForm.itemId = "";
      this.selectedItem = null;
      this.loadItemSuggestions("");
    },
    async confirmCreate() {
      if (!this.createForm.itemId || !this.createForm.quantity) return;
      this.createBusy = true;
      try {
        await this.createOrder({
          itemId: this.createForm.itemId,
          quantity: this.createForm.quantity,
          ...(this.createForm.notes ? { notes: this.createForm.notes } : {}),
        });
        this.$toast.success("Carico merce creato con successo!");
        this.showCreateModal = false;
        this.activeTab = "da_gestire";
        await this.getOrders(1);
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Errore durante la creazione."
        );
      } finally {
        this.createBusy = false;
      }
    },
  },
  watch: {
    $route(to) {
      if (to.name === "carico-merce") {
        this.getOrders(1);
      }
    },
    searchTerm() {
      this.onSearch();
    },
  },
  mounted() {
    this.getOrders(1);
  },
};
</script>

<style src="@/css/OrderPage.css" />
